import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MerchantAdminApi } from '../Api';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { ArticleSearch, Article } from './ArticleSearch';

interface ExternalPOSProductManagerProps {
  api: MerchantAdminApi;
  articleIds: string[];
  onRemoveArticle: (articleId: string) => void;
  onAddArticle: (articleId: string) => void;
}

export const ExternalPOSProductManager: React.FC<ExternalPOSProductManagerProps> = ({
  api,
  articleIds,
  onRemoveArticle,
  onAddArticle
}) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [newlyAddedIds, setNewlyAddedIds] = useState<Set<string>>(new Set());
  const { t } = useTranslation();

  // Use a ref to track which article IDs we've already fetched
  const fetchedArticleIds = useRef<Set<string>>(new Set());

  const fetchNewArticles = useCallback(async (idsToFetch: string[]) => {
    if (!api || idsToFetch.length === 0) return;

    setLoading(true);
    try {
      const sortedIds = [...idsToFetch].sort((a, b) => Number(a) - Number(b));

      const chunkSize = 500;
      const chunks = [];
      for (let i = 0; i < sortedIds.length; i += chunkSize) {
        chunks.push(sortedIds.slice(i, i + chunkSize));
      }

      const allArticles: Article[] = [];
      for (const chunk of chunks) {
        const result = await api.fetchPOSExternalArticlesById(
          chunk.map(id => Number(id))
        );
        const newArticles = result.payload.products.map(article => ({
          id: article.id,
          name: article.name,
          product_price_lists: article.product_price_lists
        }));
        allArticles.push(...newArticles);
      }

      idsToFetch.forEach(id => fetchedArticleIds.current.add(id));

      setArticles(prevArticles => {
        // Create a map of existing articles for quick lookup
        const existingArticlesMap = new Map(prevArticles.map(a => [a.id, a]));

        // Add new articles to the map
        allArticles.forEach(article => {
          existingArticlesMap.set(article.id, article);
        });

        // Convert map back to array and sort by name
        return Array.from(existingArticlesMap.values())
          .sort((a, b) => Number(a.id) - Number(b.id));
      });
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    // Find IDs that haven't been fetched yet
    const newIds = articleIds.filter(id => !fetchedArticleIds.current.has(id));
    if (newIds.length > 0) {
      fetchNewArticles(newIds);
    }
  }, [articleIds, fetchNewArticles]);

  const handleAddArticle = (article: Article) => {
    if (!articleIds.includes(article.id)) {
      onAddArticle(article.id);
      // Add the article directly to the list without fetching
      setArticles(prevArticles => {
        const existingArticlesMap = new Map(prevArticles.map(a => [a.id, a]));
        existingArticlesMap.set(article.id, article);
        return Array.from(existingArticlesMap.values())
          .sort((a, b) => Number(a.id) - Number(b.id));
      });
      fetchedArticleIds.current.add(article.id);
      // Add to newly added set
      setNewlyAddedIds(prev => new Set(prev).add(article.id));
    }
  };

  const handleRemove = (articleId: string) => {
    setArticles(prevArticles => prevArticles.filter(a => a.id !== articleId));
    fetchedArticleIds.current.delete(articleId);
    onRemoveArticle(articleId);
    // Remove from newly added set
    setNewlyAddedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(articleId);
      return newSet;
    });
  };

  const handleSearch = async (term: string) => {
    const result = await api.searchPOSExternalArticles(term);
    return result.payload.products.map(article => ({
      id: article.id,
      name: article.name,
      product_price_lists: article.product_price_lists
    }));
  };

  if (loading) {
    return <div>{t('externalPOSProductManager.loading')}</div>;
  }

  return (
    <Row className="g-3">
      <Col md={6} className="order-1 order-md-1">
        {articles.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">{t('externalPOSProductManager.totalProducts')}: {articles.length}</small>
              {articles.length > 6 && (
                <small className="text-muted">
                  <FontAwesomeIcon icon={["fas", "chevron-down"]} className="me-1" />
                  {t('externalPOSProductManager.scrollToSeeMore')}
                </small>
              )}
            </div>
            <div className="scrollable-container" style={{ maxHeight: '450px', overflowY: 'auto' }}>
              {articles.map(article => (
                <Card key={article.id} className="mb-2">
                  <Card.Body className='p-1'>
                    <Row className="align-items-center">
                      <Col>
                        <div className="d-flex align-items-center">
                          <div className="fw-bold">{article.name} {newlyAddedIds.has(article.id) && (
                            <small className="ml-1 text-muted">
                              <FontAwesomeIcon icon={["fal", "check-circle"]} />
                            </small>
                          )}</div>
                        </div>
                        <div className="small text-muted">
                          {t('externalPOSProductManager.id')}: {article.id}
                          {article.product_price_lists && article.product_price_lists.length > 0 && (
                            <>
                              <br />
                              {t('externalPOSProductManager.priceLists')}: {article.product_price_lists.join(', ')}
                            </>
                          )}
                        </div>
                      </Col>
                      <Col xs="auto">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="border-0"
                          onClick={() => handleRemove(article.id)}
                        >
                          <FontAwesomeIcon icon={["fas", "trash-alt"]} />
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </>
        )}

        {articles.length === 0 && (
          <Card>
            <Card.Body className="text-center py-4">
              <div className="mb-2">
                <FontAwesomeIcon icon={["fas", "box-open"]} size="2x" className="text-muted" />
              </div>
              <div className="text-muted">
                {t('externalPOSProductManager.noProducts')}
              </div>
            </Card.Body>
          </Card>
        )}
      </Col>

      <Col md={6} className="order-2 order-md-2">
        <div style={{ position: 'relative' }}>
          <ArticleSearch
            onSearch={handleSearch}
            onSelect={handleAddArticle}
            disabledIds={articleIds}
            searchPlaceholderKey="externalPOSProductManager.searchPlaceholder"
            searchHelpKey="externalPOSProductManager.searchHelp"
            searchResultsKey="externalPOSProductManager.searchResults"
            idKey="externalPOSProductManager.id"
            priceListsKey="externalPOSProductManager.priceLists"
            selectButtonIcon={["fas", "plus"]}
            selectButtonVariant="outline-success"
            onSelectCallback={() => {
              // Additional cleanup if needed
            }}
          />
        </div>
      </Col>
    </Row>
  );
}; 